import { useNavigate } from 'react-router-dom';
import { useMySchedule } from '../hooks/useMySchedule';
import { MiniCalendar } from '../components/MiniCalendar';
import { CategoryColorSwatch } from '../components/CategoryColorSwatch';
import { ApiError } from '../lib/api';
import { useIsMobile } from '../hooks/useIsMobile';
import { MyScheduleMobilePage } from './mobile/MyScheduleMobilePage';

function weekLabel(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('default', { month: 'long', day: 'numeric', year: 'numeric' });
}

function groupByWeek(days: string[], rows: ReturnType<typeof useMySchedule>['data']) {
  if (!rows || rows.rows.length === 0) return [];

  const personRow = rows.rows[0];
  const items: { weekStart: string; date: string; assignments: (typeof personRow.cells)[0]['items'] }[] = [];

  for (const day of days) {
    const cell = personRow.cells.find((c) => c.date === day);
    if (cell && cell.items.length > 0) {
      items.push({ weekStart: getWeekStart(day), date: day, assignments: cell.items });
    }
  }
  return items;
}

function getWeekStart(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay();
  const start = new Date(d.getTime() - dow * 86400_000);
  return start.toISOString().slice(0, 10);
}

/**
 * Phones get a different page, not a squeezed two-column layout — see
 * {@link MyScheduleMobilePage}. The branch is a whole component swap so the desktop page's
 * mini-calendar never mounts on a phone (and vice versa); rotating a tablet past 768px
 * switches live.
 */
export function MySchedulePage() {
  return useIsMobile() ? <MyScheduleMobilePage /> : <MyScheduleDesktopPage />;
}

function MyScheduleDesktopPage() {
  const { data, isLoading, error } = useMySchedule();
  const navigate = useNavigate();

  const personRow = data?.rows[0] ?? null;
  const allAssignmentDates = personRow?.cells
    .filter((c) => c.items.length > 0)
    .map((c) => ({ date: c.date })) ?? [];

  const is404 = error instanceof ApiError && error.status === 404;

  const items = data ? groupByWeek(data.days, data) : [];

  const weekGroups = new Map<string, typeof items>();
  for (const item of items) {
    const existing = weekGroups.get(item.weekStart) ?? [];
    existing.push(item);
    weekGroups.set(item.weekStart, existing);
  }

  return (
    <section className="bg-white rounded-lg shadow p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-brand-blue">My Schedule</h1>
      <p className="text-sm text-brand-charcoal/70">
        Next 90 days{personRow?.region ? ` · ${personRow.region}` : ''}
      </p>

      {isLoading && (
        <div className="mt-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-slate-100 rounded animate-pulse" />
          ))}
        </div>
      )}

      {(error && !is404) && (
        <p className="mt-4 text-sm text-red-600">Something went wrong. Try again, or contact your admin if the problem persists.</p>
      )}

      {(is404 || (data && !personRow)) && (
        <p className="mt-4 text-sm text-slate-500">
          Your account is not linked to a crew member profile. No schedule data is available.
        </p>
      )}

      {!isLoading && !error && personRow && (
        <div className="flex gap-6 mt-6">
          <div className="flex-1 space-y-4">
            {weekGroups.size === 0 ? (
              <p className="text-sm text-slate-500">No assignments scheduled in the next 90 days.</p>
            ) : (
              Array.from(weekGroups.entries()).map(([weekStart, dayItems]) => (
                <div key={weekStart}>
                  <h2 className="text-xs font-semibold text-brand-charcoal/60 uppercase tracking-wide mb-2">
                    Week of {weekLabel(weekStart)}
                  </h2>
                  <div className="space-y-2">
                    {dayItems.flatMap((di) =>
                      di.assignments.map((a) => (
                        <div
                          key={`${di.date}-${a.assignmentId}`}
                          className="bg-white rounded-lg border border-slate-200 p-4 flex gap-3 cursor-pointer hover:border-brand-blue/40 transition-colors"
                          onClick={() => navigate(`/projects/${a.jobNumber}`)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/projects/${a.jobNumber}`); }}
                        >
                          <div className="w-3 rounded-full shrink-0" style={{ backgroundColor: a.colorHex }} />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-brand-charcoal truncate">{a.projectName}</div>
                            <div className="text-xs text-brand-charcoal/60">{a.jobNumber}</div>
                            <div className="text-xs text-slate-600 mt-0.5">{di.date}</div>
                            <div className="flex gap-2 mt-1 flex-wrap">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-brand-charcoal">{a.categoryName}</span>
                              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-brand-charcoal">
                                {a.isBillable ? 'Billable' : 'Non-billable'}
                              </span>
                              {a.isPtoEquivalent && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-brand-charcoal">PTO-equivalent</span>
                              )}
                            </div>
                          </div>
                          <CategoryColorSwatch colorHex={a.colorHex} isPtoEquivalent={a.isPtoEquivalent} />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="w-72 shrink-0 space-y-4">
            <MiniCalendar
              assignments={allAssignmentDates}
              month={new Date()}
            />
          </div>
        </div>
      )}
    </section>
  );
}
